import PropTypes from "prop-types";
import { useEffect, useMemo, useState, useRef } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
// material
import { alpha, styled, useTheme } from "@mui/material/styles";
import { Box, Drawer, Typography } from "@mui/material";

import FormControl from "@mui/material/FormControl";
import SvgIcon from "@mui/material/SvgIcon";
// components
import Scrollbar from "../../components/Scrollbar";
import NavSection from "../../components/NavSection";
import { MHidden } from "../../components/@material-extend";
import sidebarConfig from "./SidebarConfig";
import { useAuth } from "src/hooks/useAuth.hook";
import { teamsSlice } from "src/store/teams/teams.slice";
import { useDispatch } from "react-redux";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { is } from "src/util/is";
import OazaLogo from "../../image/logo/Logo.png";
import { FEATURE } from "src/util/enums";
import TeamsModal from "./TeamModal";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 250;
const COLLAPSED_WIDTH = 110; // Increased from 100 to 110 for better logo visibility

const RootStyle = styled("div")(({ theme, isCollapsed }) => ({
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const surfaceStyles = (theme) => ({
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.background.paper
      : theme.palette.background.elevated,
  border:
    theme.palette.mode === "light"
      ? "1px solid transparent"
      : `1px solid ${alpha(theme.palette.divider, 0.85)}`,
  boxShadow:
    theme.palette.mode === "light"
      ? "0px 10px 24px rgba(15, 23, 42, 0.08)"
      : "0px 12px 32px rgba(0, 0, 0, 0.55)",
});

const CircleIcon = () => {
  const theme = useTheme();
  const brand = theme.palette.primary.main;
  return (
    <SvgIcon sx={{ fontSize: 20, mr: 1 }}>
      <circle cx="10" cy="10" r="9" fill={alpha(brand, 0.35)} />
      <circle cx="10" cy="10" r="4.5" fill={brand} />
    </SvgIcon>
  );
};

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  isCollapsed: PropTypes.bool,
};

const notASideBarLink = [
  "Finance",
  "ChargeZone",
  "Settings",
]; //ARRAY OF FEATUREs NOT TO BE SHOWN AS A SIDEBAR LINK

const getCompanyLogo = (data, id) => {
  const { company, role } = data;
  const updatedTeamArr = [
    { teamId: company.id, companyLogo: company.companyLogo },
    ...role,
  ];
  const currentTeamObj = updatedTeamArr.find(
    (team) => Number(team.teamId) === Number(id)
  );
  return currentTeamObj.companyLogo ? currentTeamObj.companyLogo : null;
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar, isCollapsed }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const team = useSelectedTeam();
  const [logo, setLogo] = useState();
  const anchorRef = useRef(null);
  const [isTeamsModalOpen, setTeamsModalOpen] = useState(false);

  const handleOpenTeamsModal = () => {
    setTeamsModalOpen(true);
  };

  const handleCloseTeamsModal = () => {
    setTeamsModalOpen(false);
  };

  const handleSelectTeam = (team) => {
    // Retain team capability logic
    dispatch(teamsSlice.actions.setTeamId(team.teamId));
    dispatch(teamsSlice.actions.setTeamState(team?.teamState));
    dispatch(teamsSlice.actions.setTeamCountry(team?.teamCountry));
    localStorage.setItem('selectedTeamId', JSON.stringify(team.teamId));
    setTeamsModalOpen(false);
    navigate("/dashboard");
  };

  let selectedTeamFromLS = null;
  try {
    const storedData = localStorage.getItem("selectedTeamId");
    selectedTeamFromLS =
      storedData && storedData !== "undefined" ? JSON.parse(storedData) : null;
  } catch (error) {
    console.error("Error parsing selectedTeamId from localStorage:", error);
  }

  const company = useMemo(() => {
    return user?.role?.find((team) => team.teamId === user?.company?.id);
  }, [user]);

  const companyType = useMemo(() => {
    return user?.company?.companyType;
  }, [user]);

  useEffect(() => {
    if (!is.empty(team)) {
      if (companyType === "GP" && Number(team) !== Number(user?.company?.id)) {
        setLogo(() => getCompanyLogo(user, team.teamId));
      } else {
        setLogo(user?.company?.companyLogo);
      }
    }
  }, [companyType, team, user]);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (selectedTeamFromLS) {
      dispatch(teamsSlice.actions.setTeamId(selectedTeamFromLS));
      // Get team state and country from user role data
      const userRole = user?.role?.find(role => role.teamId === selectedTeamFromLS);
      dispatch(teamsSlice.actions.setTeamState(userRole?.teamState ?? null));
      dispatch(teamsSlice.actions.setTeamCountry(userRole?.teamCountry ?? null));
    } else {
      if (!team?.teamId) {
        dispatch(teamsSlice.actions.setTeamId(
          company?.teamId ?? user?.role?.[0]?.teamId ?? null
        ));
        dispatch(teamsSlice.actions.setTeamState(
          user?.role?.[0]?.teamState ?? null
        ));
        dispatch(teamsSlice.actions.setTeamCountry(
          user?.role?.[0]?.teamCountry ?? null
        ));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initialization effect runs only once intentionally
  }, []);

  const navConfig = useMemo(() => {
    const teamType = !!team?.teamType;
    // Build from access list
    let built = team?.access?.reduce((acc, item) => {
      if (
        item.view === 'Y' &&
        item.featureName === 'ChargeZone' &&
        teamType
      ) {
        acc = [...acc, { ...sidebarConfig[item.featureName], access: item }];
      } else if (
        item.view === 'Y' &&
        item.featureName &&
        !notASideBarLink.includes(item.featureName)
      ) {
        acc = [...acc, { ...sidebarConfig[item.featureName], access: item }];
      }
      return acc;
    }, []) ?? [];

    // Fallback injection for parent company (group parent) to always show Map & Promo Codes
    // Determine parent company: companyType === 'GP' OR root teamId equals user.company.id
    const isParentCompany = companyType === 'GP' || Number(team?.teamId) === Number(user?.company?.id);
    if (isParentCompany) {
      const requiredFeatures = [FEATURE.MAP, FEATURE.PROMOCODE];
      requiredFeatures.forEach(fKey => {
        const already = built.some(item => item.path === sidebarConfig[fKey]?.path);
        if (!already && sidebarConfig[fKey]) {
          built.push({ ...sidebarConfig[fKey], access: { view: 'Y', featureName: fKey } });
        }
      });
    }

    return built;
  }, [team, companyType, user]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
      }}
    >
      {/* Header with Logo */}
  <Box sx={{ px: isCollapsed ? 1 : '7px', py: isCollapsed ? 1.5 : 2, width: "100%" }}>
        {/* Logo Box */}
        <Box
          component={RouterLink}
          to="/"
          sx={(theme) => ({
            width: isCollapsed ? "80px" : { xs: "90%", sm: 236 },
            height: isCollapsed ? 80 : 72,
            ...surfaceStyles(theme),
            borderRadius: "6px",
            padding: isCollapsed ? "8px" : "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: isCollapsed ? "center" : "flex-end",
            transition: "all 0.3s ease",
            margin: "0 auto",
            "&:hover": {
              transform: "translateY(-3px)",
              boxShadow:
                theme.palette.mode === "light"
                  ? "0px 8px 20px rgba(0, 0, 0, 0.15)"
                  : "0px 16px 36px rgba(0, 0, 0, 0.7)",
            },
          })}
        >
          <img
            src={logo ? logo : OazaLogo}
            alt="Company Logo"
            style={{ 
              height: isCollapsed ? "60px" : "50px", 
              width: isCollapsed ? "60px" : "210px", 
              objectFit: "contain",
              borderRadius: isCollapsed ? "6px" : "0",
              marginRight: isCollapsed ? "0" : "8px", // Add margin when expanded
            }}
          />
        </Box>
      </Box>

      {/* Modal Box - Hide when collapsed */}
      {!isCollapsed && (
        <Box sx={{ mb: 2, mx: 2.5 }}>
          <FormControl sx={{ m: 1 }}>
            <Box
              ref={anchorRef}
              onClick={handleOpenTeamsModal}
              sx={(theme) => ({
                width: { xs: "190px", sm: 236 },
                minHeight: 42,
                ...surfaceStyles(theme),
                borderRadius: "6px",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                cursor: "pointer",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                margin: "0 auto",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow:
                    theme.palette.mode === "light"
                      ? "0px 6px 16px rgba(0, 0, 0, 0.15)"
                      : "0px 16px 32px rgba(0, 0, 0, 0.65)",
                },
              })}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CircleIcon />
                <Typography variant="body1" noWrap>
                  {team?.teamName ?? "Select a Team"}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  fontWeight: "bold",
                }}
              >
                Change
              </Typography>
            </Box>

            <TeamsModal
              open={isTeamsModalOpen}
              onClose={handleCloseTeamsModal}
              teams={user?.role ?? []}
              onSelectTeam={handleSelectTeam}
              sx={{
                "& .MuiPaper-root": (theme) => ({
                  borderRadius: "6px",
                  padding: "16px",
                  boxShadow:
                    theme.palette.mode === "light"
                      ? "0px 8px 24px rgba(0, 0, 0, 0.2)"
                      : "0px 18px 36px rgba(0, 0, 0, 0.7)",
                  maxWidth: "90%",
                  width: "400px",
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? theme.palette.background.paper
                      : theme.palette.background.elevated,
                }),
              }}
            />
          </FormControl>
        </Box>
      )}

      {/* Navigation Section with proper spacing */}
      <Box sx={{ mt: isCollapsed ? 1 : 0, width: "100%" }}>
        <NavSection navConfig={navConfig} isCollapsed={isCollapsed} />
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      {/* Footer space */}
      <Box sx={{ px: isCollapsed ? 1 : 2.5, pb: 2, mt: 5 }}></Box>
    </Scrollbar>
  );

  return (
    <RootStyle isCollapsed={isCollapsed}>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: (theme) => ({
              width: DRAWER_WIDTH,
              bgcolor: theme.palette.background.default,
            }),
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: (theme) => ({
              width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
              bgcolor: theme.palette.background.default,
              transition: "width 0.3s ease",
              overflow: "hidden",
            }),
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
